import type { AxiosError } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { ProcessingModal, ProcessingModalItem } from '@myn/mui';

type ApiError = {
  detail?: string;
  message?: string;
};
type Props = {
  onExpandButtonClick: () => void;
  onCloseButtonClick: () => void;
  isError: boolean;
  error?: AxiosError<ApiError> | null;
  isSuccess: boolean;
  isModalExpanded: boolean;
  mode: 'rollback' | 'generate';
  isDrawerOpen?: boolean;
};

const SuggestionProcessingModal = ({
  onExpandButtonClick,
  onCloseButtonClick,
  isModalExpanded,
  isError,
  isSuccess,
  error,
  mode,
  isDrawerOpen,
}: Props) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const getStatus = () => {
    if (isError) {
      return 'error';
    }
    if (isSuccess) {
      return 'success';
    }
    return 'default';
  };
  const currentStatus = getStatus();

  const errorMessage =
    error?.response?.data?.detail ?? error?.response?.data.message;
  const unexpectedError = t('Unexpected error. Please try again');

  const modalStatus = {
    rollback: {
      default: {
        message: t('Rolling back suggestion'),
        description: t('Mint.ai is rolling back a suggestion.'),
      },
      success: {
        message: t('Suggestion rolled back'),
        description: t('Mint.ai has rolled back a suggestion.'),
      },
      error: {
        message: t('Suggestion was not rolled back. Please try again'),
        description: errorMessage ?? unexpectedError,
      },
    },
    generate: {
      default: {
        message: t('Generating new suggestion'),
        description: t('Estimated time: 2 min'),
      },

      success: {
        message: t('Suggestion calculated'),
        description: t('Mint.ai has calculated a new suggestion version.'),
      },
      error: {
        message: t('New suggestion was not generated'),
        description: errorMessage ?? unexpectedError,
      },
    },
  };

  return (
    <div
      aria-label="rework-status"
      className={cn(
        'absolute bottom-16 flex flex-col',
        isDrawerOpen ? 'right-87' : 'right-3',
      )}
    >
      <ProcessingModal
        processingStatus={currentStatus}
        headerTitle={modalStatus[mode][currentStatus].message}
        onExpandButtonClick={onExpandButtonClick}
        onCloseButtonClick={onCloseButtonClick}
        isModalExpanded={isModalExpanded}
      >
        <ProcessingModalItem
          processingItemStatus={
            currentStatus === 'default' ? undefined : currentStatus
          }
        >
          <p className={typographyVariants({ variant: 'bodyShort01' })}>
            {modalStatus[mode][currentStatus].description}
          </p>
        </ProcessingModalItem>
      </ProcessingModal>
    </div>
  );
};

export default SuggestionProcessingModal;
